import { useEffect, useState } from 'react';
import {
  fmtClock,
  fmtDbfs,
  fmtDur,
  fmtMs,
  useCan,
  useCaptions,
  useCommand,
  useDevice,
  useListen,
  useLive,
  useSettings,
  useStatus,
} from '@glyph/core';
import {
  Button,
  Card,
  CardHeader,
  IconButton,
  KeyValueRow,
  ListRow,
  OledDisplay,
  PageHeader,
  PencilIcon,
  Section,
  SegmentMeter,
  StackedActionButton,
  StatusPill,
  Tag,
  VideoFramePreview,
  stateTone,
  useClock,
  useToast,
} from '@glyph/ui';
import {
  UPLINK_LABEL,
  bgLabel,
  captionLines,
  deviceStateOf,
  displayToMock,
  fmtMode,
  fmtTc,
  fmtTs,
  isBlank,
  outputByName,
  upper,
} from './shared';
import { RenameDeviceDialog } from './RenameDeviceDialog';
import { ScheduleBadge } from './SchedulesScreen';
import { NotificationsCard } from './NotificationsCard';

export interface LiveScreenProps {
  deviceId?: string;
}

/** Live: caption preview (text mock, same as the Captions page), Captions/Record/Listen/Clear/Blank, LED meter, transcript, status rows, OLED sim. */
export function LiveScreen({ deviceId }: LiveScreenProps) {
  const dev = useDevice(deviceId);
  const live = useLive(deviceId);
  const status = useStatus(deviceId);
  const caps = useCaptions(deviceId);
  const settings = useSettings(deviceId);
  const listen = useListen(deviceId);
  const cmd = useCommand(deviceId);
  const toast = useToast();
  const clock = useClock();
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (cmd.lastError) toast.push(cmd.lastError.message, 'bad');
  }, [cmd.lastError, toast]);

  const st = status.data;
  const t = live.telemetry;
  const h = live.health;
  const d = dev.data;
  const s = settings.data;
  const disp = s?.display;
  const running = st?.engine?.running ?? t?.engine_running ?? false;
  const blank = isBlank(st);
  const rec = st?.record;
  const level = (d?.online ?? true) ? (live.audioLevel[0] ?? -60) : -60; // nothing to meter on a unit that is off
  const state = deviceStateOf(st, d?.online ?? true);
  const online = d?.online ?? true;
  const lat = fmtMs(st?.engine?.rtt_ms);
  const nameU = upper(d?.name ?? 'GLYPH'); // the real OLED shows the device name, never an id
  const canRename = useCan('device.rename');
  const [renaming, setRenaming] = useState(false);
  const outs = st?.outputs ?? live.snapshot?.outputs;
  const outA = outputByName(outs, 'HDMI-A');
  const outB = outputByName(outs, 'HDMI-B');
  const mirror = (s?.outputs.policy ?? 'mirror') === 'mirror';
  const lines = disp && running && !blank ? captionLines(caps.finals, caps.partial, disp) : [];
  const uplink = t?.uplink ?? st?.network?.uplink ?? 'none';
  const net = st?.network;
  const cloud = st?.cloud;
  const agent = st?.version?.agent ?? t?.agent_version ?? d?.agent_version ?? '—';
  const slot = st?.version?.slot ?? d?.slot ?? '—';

  const statusRows: { k: string; val: string; sub: string; ok: boolean }[] = [
    {
      k: 'Captions',
      val: `Deepgram ${st?.engine?.model ?? s?.deepgram.model ?? '—'}`,
      sub: running
        ? `connected · ${lat} round trip · interim ${s?.deepgram.interim_results ? 'on' : 'off'}`
        : 'idle · session closed',
      ok: running && (h?.deepgram.state ?? 'ok') === 'ok',
    },
    {
      k: 'Audio in',
      val: s?.audio.device || h?.audio.message || '—',
      sub: `${(s?.audio.sample_rate ?? 48000) / 1000} kHz · ch ${s?.audio.program_channel ?? 1} · ${fmtDbfs(level)} dBFS`,
      ok: (h?.audio.state ?? 'ok') === 'ok',
    },
    {
      k: 'Outputs',
      val: `HDMI-A ${fmtMode(outA)}`,
      sub: outB?.connected
        ? mirror
          ? 'HDMI-B mirroring A'
          : `HDMI-B independent · ${outB.layout}`
        : 'HDMI-B not connected',
      ok: !!outA?.connected,
    },
    {
      k: 'Latency',
      val: online ? `cloud ${fmtMs(d?.cloud_rtt_ms)} · Deepgram ${lat}` : 'offline',
      sub: 'cloud ↔ unit round trip · audio sent → words back',
      ok: online,
    },
    {
      k: 'Uplink',
      val: uplink === 'none' ? 'No uplink' : `${UPLINK_LABEL[uplink]} · ${net?.address ?? ''}`,
      sub:
        uplink === 'eth'
          ? `gw ${net?.eth.gateway ?? '—'} · DNS ${net?.eth.dns.join(', ') || '—'} · Wi-Fi ${net?.wifi.state ?? 'standby'}`
          : uplink === 'wifi'
            ? `${net?.wifi.ssid ?? ''} · ${net?.wifi.rssi_dbm ?? '—'} dBm`
            : 'captions keep running on cached credentials',
      ok: uplink !== 'none',
    },
    {
      k: 'Hotspot',
      val: net?.ap.enabled ? net.ap.ssid : 'Off',
      sub: net?.ap.enabled ? `${net.ap.clients.length} clients · ${net.ap.ip}` : 'disabled',
      ok: !!net?.ap.enabled,
    },
    {
      k: 'Cloud',
      val: cloud ? cloud.url.replace(/^wss?:\/\//, '').split('/')[0]! : '—',
      sub: cloud?.connected
        ? `heartbeat ${cloud.heartbeat_s} s · RTT ${cloud.rtt_ms ?? '—'} ms · Ed25519`
        : 'not connected',
      ok: !!cloud?.connected,
    },
    {
      k: 'System',
      val: `Pi 5 · agent ${agent} · slot ${slot}`,
      sub: t ? `CPU ${t.temp_c ?? '—'} °C · ${t.cpu_pct} % · up ${fmtDur(t.uptime_s)}` : '',
      ok: !t?.under_voltage && !t?.throttled,
    },
  ];
  // Gen 1 fail-safes (docs/PLAN.md reconciliation: no local engine; ring replay + partner follow instead).
  const refresh = () => void status.refetch();
  const togCaptions = () => void cmd.run(running ? 'engine.stop' : 'engine.start', {}).then(refresh);
  const togRec = () => void cmd.run(rec?.active ? 'record.stop' : 'record.start', {}).then(refresh);
  const togBlank = () => void cmd.run('display.overlay', { kind: blank ? 'none' : 'blank' }).then(refresh);
  const clear = () => void cmd.run('captions.clear', {});
  const recClock = rec?.active ? fmtClock(rec.elapsed_s) : 'OFF';

  const frame = (
    <VideoFramePreview
      background={blank ? 'boot' : 'chroma'}
      bootColor={disp?.key_color}
      mock={disp ? displayToMock(disp, lines) : undefined}
      topLeft={outA?.connected ? `HDMI-A · ${outA.width}×${outA.height}p${outA.refresh_hz}` : 'HDMI-A'}
      topRight={bgLabel(disp?.key_color ?? '#00B140', blank)}
      bottomRight={
        rec?.active && st ? fmtTc(st.timecode?.tc ?? '00:00:00:00', 0, st.timecode?.framerate) : undefined
      }
      cornerMarks
    />
  );
  const listenNote = listen.active
    ? `MONITORING IN BROWSER · OPUS ${listen.stats?.decoder === 'webcodecs' ? '(WEBCODECS)' : '24 KB/S'} · ${listen.behindMs} MS BEHIND LIVE · NOT THE CAPTION PATH`
    : '';
  const oled = (
    <OledDisplay
      line1={{ left: nameU, right: upper(state) }}
      line2={{
        left: `ASR ${st?.engine?.model ?? '—'} ${lat}`,
        right: rec?.active ? `REC ${fmtClock(rec.elapsed_s)}` : clock,
      }}
      vu={[Math.max(0, (level + 60) / 60), 0]}
      rec={!!rec?.active}
    />
  );

  return (
    <Section>
      <RenameDeviceDialog open={renaming} onClose={() => setRenaming(false)} deviceId={deviceId} currentName={d?.name ?? ''} serial={d?.serial} currentSsid={net?.ap.ssid ?? d?.hotspot_ssid} />
      <PageHeader
        desktopOnly
        title={
          <span className="group inline-flex items-center gap-2">
            {d?.name ?? '—'}
            {canRename && (
              <IconButton framed={false} size="md" label={`Rename ${d?.name ?? 'unit'}`} onClick={() => setRenaming(true)} className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100">
                <PencilIcon />
              </IconButton>
            )}
          </span>
        }
        badge={
          <Tag tone={stateTone(state)} large>
            {state}
          </Tag>
        }
        meta={`${d?.assignment?.event_name ?? 'Unassigned'} · ${UPLINK_LABEL[uplink]}${net?.address ? ` · ${net.address}` : ''} · REC ${recClock}`}
        actions={
          <>
            <ScheduleBadge deviceId={deviceId} className="self-center" />
            <Button
              variant="toggle"
              on={running}
              onClick={togCaptions}
              disabled={!!cmd.pending['engine.start'] || !!cmd.pending['engine.stop'] || !!st?.engine?.locked}
            >
              {running ? 'Captions ON' : 'Captions OFF'}
            </Button>
            <Button variant="toggle" on={!!rec?.active} onBg="var(--bad)" onColor="#fff" onClick={togRec}>
              {rec?.active ? '● Recording' : '○ Record'}
            </Button>
            <Button
              variant="toggle"
              on={listen.active}
              onBg="var(--accent)"
              onClick={() => void listen.toggle()}
            >
              {listen.active ? '■ Stop listening' : '▶ Listen'}
            </Button>
            <Button onClick={clear}>Clear</Button>
            <Button variant="toggle" on={blank} onBg="var(--key)" onClick={togBlank}>
              {blank ? 'Blank ON' : 'Blank output'}
            </Button>
          </>
        }
      />

      {/* mobile */}
      <div className="flex flex-col gap-4 md:hidden">
        {frame}
        {canRename && (
          <Card>
            <ListRow
              label="Unit"
              title={d?.name ?? '—'}
              sub={net?.ap.enabled ? net.ap.ssid : 'hotspot off'}
              trailing={
                <IconButton framed={false} size="mobile" label={`Rename ${d?.name ?? 'unit'}`} onClick={() => setRenaming(true)}>
                  <PencilIcon />
                </IconButton>
              }
            />
          </Card>
        )}
        <div className="grid grid-cols-4 gap-2">
          <StackedActionButton
            label="Captions"
            state={running ? 'ON' : 'OFF'}
            on={running}
            onClick={togCaptions}
          />
          <StackedActionButton
            label="Record"
            state={recClock}
            on={!!rec?.active}
            onBg="var(--bad)"
            onColor="#fff"
            onClick={togRec}
          />
          <StackedActionButton
            label="Listen"
            state={listen.active ? 'ON' : 'OFF'}
            on={listen.active}
            onBg="var(--accent)"
            onColor="#0A0A0B"
            onClick={() => void listen.toggle()}
          />
          <StackedActionButton
            label="Blank"
            state={blank ? 'ON' : 'OFF'}
            on={blank}
            onBg="var(--key)"
            onColor="#0A0A0B"
            onClick={togBlank}
          />
        </div>
        <Card>
          <div className="flex items-center gap-3 px-4 py-3.5">
            <span className="font-mono text-sm text-fg4">INPUT</span>
            <SegmentMeter dbfs={level} className="flex-1" />
            <span className="min-w-[64px] flex-none text-right font-mono text-sm text-fg3 tabular">
              {fmtDbfs(level)} dBFS
            </span>
          </div>
          {listen.active && (
            <div className="px-4 pb-3 font-mono text-xs font-medium leading-[1.5] tracking-[.04em] text-accent">
              MONITORING IN BROWSER · OPUS · {listen.behindMs} MS BEHIND LIVE
            </div>
          )}
          {statusRows.map((r) => (
            <ListRow
              key={r.k}
              label={r.k}
              title={r.val}
              sub={r.sub}
              trailing={<StatusPill tone={r.ok ? 'ok' : 'fg5'}>{r.ok ? 'OK' : 'OFF'}</StatusPill>}
            />
          ))}
        </Card>
        <Card>
          <CardHeader
            title="Transcript"
            actions={
              <Button size="xs" variant="ghost" mono onClick={clear}>
                CLEAR SCREEN
              </Button>
            }
          />
          {caps.finals.slice(-3).map((f) => (
            <div key={f.id} className="flex flex-col gap-1 border-t border-line px-4 py-2.5">
              <span className="font-mono text-sm text-fg4">{fmtTs(f.ts)}</span>
              <span className="text-lg font-normal leading-[1.4] text-fg2">{f.text}</span>
            </div>
          ))}
          <div className="flex flex-col gap-1 border-t border-line px-4 py-2.5">
            <span className="meta text-accent">INTERIM</span>
            <span className="text-lg font-normal leading-[1.4] text-fg">{caps.partial}</span>
          </div>
        </Card>
        <NotificationsCard deviceId={deviceId} />
        {oled}
      </div>

      {/* desktop */}
      <div className="hidden flex-wrap items-start gap-5 md:flex">
        <div className="flex min-w-0 flex-col gap-3.5" style={{ flex: '1 1 560px' }}>
          {frame}
          <Card className="flex flex-wrap items-center gap-4 px-4 py-3.5">
            <div className="flex min-w-[150px] flex-col gap-[5px]">
              <span className="micro text-fg4">Input</span>
              <span className="text-base font-normal leading-none text-fg2">
                {s?.audio.device || '—'} · ch {s?.audio.program_channel ?? 1} ·{' '}
                {(s?.audio.sample_rate ?? 48000) / 1000} kHz
              </span>
            </div>
            <SegmentMeter dbfs={level} className="flex-1 basis-[200px]" />
            <span className="min-w-[70px] text-right font-mono text-sm text-fg3 tabular">
              {fmtDbfs(level)} dBFS
            </span>
            {listenNote && <span className="meta basis-full text-accent">{listenNote}</span>}
            {listen.error && (
              <span className="meta basis-full text-warn">LISTEN FAILED · {listen.error}</span>
            )}
          </Card>
          <Card>
            <CardHeader
              dense
              title="Transcript"
              meta={`${st?.engine?.model ?? '—'} · ${lat}${rec?.active ? ` · WRITING TO ${rec.name}` : ''}`}
            />
            {caps.finals.slice(-3).map((f) => (
              <div
                key={f.id}
                className="grid grid-cols-[110px_minmax(0,1fr)] gap-3.5 border-t border-line px-4 py-[9px]"
              >
                <span className="font-mono text-sm leading-[1.5] text-fg4 tabular">{fmtTs(f.ts)}</span>
                <span className="text-md font-normal leading-[1.5] text-fg2">{f.text}</span>
              </div>
            ))}
            <div className="grid grid-cols-[110px_minmax(0,1fr)] gap-3.5 border-t border-line px-4 py-[9px]">
              <span className="meta leading-[1.6] text-accent">INTERIM</span>
              <span className="text-md font-normal leading-[1.5] text-fg">
                {caps.partial}
                <span className="ml-[3px] inline-block h-[13px] w-[2px] animate-blink bg-accent align-[-2px]" />
              </span>
            </div>
          </Card>
        </div>
        <div className="flex min-w-[280px] flex-col gap-5" style={{ flex: '0 1 320px' }}>
          <Card>
            {statusRows.map((r, i) => (
              <KeyValueRow
                key={r.k}
                k={r.k}
                value={r.val}
                sub={r.sub}
                subTone={r.ok ? 'fg4' : 'warn'}
                className={i === 0 ? 'border-t-0' : ''}
              />
            ))}
          </Card>
          {oled}
          <NotificationsCard deviceId={deviceId} dense />
        </div>
      </div>
    </Section>
  );
}
